import { Injectable, InjectionToken } from '@angular/core';
import {
  CsHttpClientService,
  StorageService
} from '@cbuild/cs-common-data-services';
// @ts-ignore (https://github.com/firebase/firebase-js-sdk/pull/1206)
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Observable, of, Subject, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';

export const FIREBASE_CONFIG = new InjectionToken('firebase-config');

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  // tslint:disable-next-line: no-any
  public messaging!: any;
  private initialized = false;
  private foregroundMessages$ = new Subject<any>();
  constructor(
    private http: CsHttpClientService,
    private storageService: StorageService,
    private logger: LoggerService
  ) {}

  public async initialize(): Promise<void> {
    if (!this.initialized) {
      await this.requestPermission(); // Request permission on init

      this.listenForMessages(); // Listen for incoming messages
      this.initialized = true;
    }
  }

  // Listen for incoming messages
  listenForMessages(): void {
    const firebaseMessaging = getMessaging();
    onMessage(firebaseMessaging, (messagePayload: any) => {
      this.foregroundMessages$.next(messagePayload);
    });
  }

  /**
   * request permission for notification from firebase cloud messaging
   */

  private async requestPermission(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        await this.refreshFcmToken();
      } catch (error) {
        this.logger.logError('Service Worker Registration Failed.');
      }
    }
  }

  /**
   * on token refresh
   */
  private async refreshFcmToken(): Promise<void> {
    try {
      const fireMessaging = getMessaging();
      const token = await getToken(fireMessaging, {
        vapidKey: environment.firebaseConfig.vapidKey
      });

      if (token) {
        const currentToken = this.storageService.getItem('fcmToken');
        if (!currentToken) {
          this.saveFcmToken(token)
            .pipe(take(1))
            .subscribe(() => {
              this.storageService.setItem('fcmToken', token);
            });
        } else if (token !== currentToken) {
          this.updateFcmToken(token)
            .pipe(take(1))
            .subscribe(() => {
              this.storageService.setItem('fcmToken', token);
            });
        }
      } else {
        this.logger.logError(
          'No Instance ID token available. Request permission to generate one.'
        );
      }
    } catch (err) {
      this.logger.logError('Error refreshing FCM token.');
    }
  }

  private saveFcmToken(fcmToken: string): Observable<void> {
    return this.http.writeText<void>('api/notifications/fcmToken/save', {
      token: fcmToken,
      source: 'Web'
    });
  }

  private updateFcmToken(token: string): Observable<void> {
    return this.http.writeText<void>('api/notifications/fcmToken/update', {
      newToken: token,
      oldToken: this.storageService.getItem('fcmToken')
    });
  }

  public deleteFcmToken(apiKey: string): Observable<void> {
    this.initialized = false;
    if (this.storageService.getItem('fcmToken')) {
      return this.http.writeText<void>(
        'api/notifications/fcmToken/delete',
        {
          token: this.storageService.getItem('fcmToken')
        },
        undefined,
        {
          'X-API-Key': `${apiKey}`
        }
      );
    } else {
      return of(undefined);
    }
  }

  public get messages(): Observable<object> {
    return this.foregroundMessages$.asObservable();
  }
}
